import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useQuery } from '../lib/query';
import { useOptionalAuth } from '../lib/auth';
import { isEmbeddedAdmin, openTopLevel, planGateAllows } from '../lib/embedded';
import { ART_BLOCK_COPY, ART_BLOCK_HEADLINE, resumeText, type ArtBlockReasonCode } from '../../shared/artAvailability';

export const ART_AVAILABILITY_PATH = '/api/billing/availability';

export interface ArtAvailabilityResponse {
  blocked: boolean;
  reason: ArtBlockReasonCode | null;
  resumesAt: string | null;
  shopifyBilling: boolean;
}

/**
 * Astra pre-launch P1#2: one banner on every merchant page (Shopify admin and www.perzo.art) while shoppers
 * cannot create new art, derived from the store's CURRENT state (GET /api/billing/availability, the same rule as
 * admission), never from old alert rows: "Shoppers can't create new art: <reason>", the one action, and when it
 * resumes by itself when known. Re-read every minute and after any billing change (invalidate('/api/billing')).
 */
export function ArtAvailabilityBanner() {
  const embedded = useOptionalAuth()?.embedded ?? null;
  const location = useLocation();
  const navigate = useNavigate();
  const q = useQuery<ArtAvailabilityResponse>(ART_AVAILABILITY_PATH, { refetchInterval: 60_000 });
  const a = q.data;
  if (!a?.blocked || !a.reason) return null;
  // The plan screen itself already asks for a plan (App Home without a usable plan): no second ask on top of it.
  if ((a.reason === 'no_plan' || a.reason === 'plan_inactive') && !planGateAllows(embedded?.plan, location.pathname)) return null;
  const copy = ART_BLOCK_COPY[a.reason];
  const resume = resumeText(a.reason, a.resumesAt ? new Date(a.resumesAt) : null, new Date());
  const inAdmin = isEmbeddedAdmin();
  const shop = embedded?.shop ?? null;

  let action: ReactNode = null;
  const button = 'btn btn--secondary btn--sm';
  if (copy.action === 'enable_extras' || copy.action === 'raise_cap') {
    action = (
      <Link to="/app/billing#limits" className={button} data-testid="art-blocked-cta">
        {copy.label}
      </Link>
    );
  } else if (copy.action === 'choose_plan') {
    const url = embedded?.plan?.selectionUrl ?? null;
    action = (
      <button type="button" className={button} data-testid="art-blocked-cta" onClick={() => (url ? openTopLevel(url) : navigate('/app/billing'))}>
        {copy.label}
      </button>
    );
  } else if (copy.action === 'shopify_billing') {
    const url = shop ? `https://admin.shopify.com/store/${encodeURIComponent(shop.replace(/\.myshopify\.com$/, ''))}/settings/billing` : null;
    action = url ? (
      <button type="button" className={button} data-testid="art-blocked-cta" onClick={() => openTopLevel(url)}>
        {copy.label}
      </button>
    ) : (
      <Link to="/app/billing" className={button} data-testid="art-blocked-cta">
        {copy.label}
      </Link>
    );
  } else if (copy.action === 'support') {
    action = (
      <Link to="/app/support" className={button} data-testid="art-blocked-cta">
        {copy.label}
      </Link>
    );
  }
  return (
    <div className={inAdmin ? 'p-banner p-banner--critical art-blocked' : 'callout callout--danger art-blocked'} role="alert" data-testid="art-blocked-banner" data-reason={a.reason}>
      <AlertTriangle size={16} aria-hidden="true" />
      <span>
        <strong>{ART_BLOCK_HEADLINE}:</strong> {copy.reason}.{resume ? ` ${resume}` : ''} {action}
      </span>
    </div>
  );
}
